import dayjs from 'dayjs';
import Big from 'big.js';
import { useState, useEffect, useMemo } from 'react';
import { fetchDepositsWithdraws } from './depositsWithdraws';
import { fetchReferrals } from './referrals';
import { fetchTrades } from './trades';
import { fetchStakingTaxRecords } from './stakingTaxRecords';
import { fetchPaymentsTaxRecords } from './paymentsTaxRecords';
import { fetchKickbackTaxRecords } from './kickbackTaxRecords';

export const formatAmount = (amount, decimalPrecision) => {
  try {
    return amount && new Big(amount).round(decimalPrecision ?? 18, Big.roundDown).toFixed();
  } catch (e) {
    console.error(`cannot format amount: ${amount} with decimal precision ${decimalPrecision}`);
    throw e;
  }
};

export const formatDate = date => date.toISOString();

export const formatRow = assetsData => row => ({
  ...row,
  inAmount: formatAmount(row.inAmount, assetsData[row.inCurrency]?.decimalPrecision),
  outAmount: formatAmount(row.outAmount, assetsData[row.outCurrency]?.decimalPrecision),
  formattedDate: formatDate(row.created)
});

const filterMainAssets = assetsData =>
  Object.fromEntries(Object.entries(assetsData).filter(([asset]) => !asset.includes('TEST')));

export const useAnnualReport = (skip, accountId, year, assetsData) => {
  const [result, setResult] = useState({ loading: false, data: undefined, error: undefined });
  const assets = useMemo(() => filterMainAssets(assetsData), [assetsData]);

  useEffect(() => {
    if (skip) return;
    setResult({ loading: true, data: null, error: null });
    const startTime = dayjs().set('year', year).startOf('year');
    const endTime = dayjs().set('year', year).endOf('year');

    Promise.all([
      fetchReferrals(year),
      fetchTrades(accountId, startTime, endTime),
      fetchDepositsWithdraws(startTime, endTime, assets),
      fetchStakingTaxRecords(year),
      fetchPaymentsTaxRecords(year),
      fetchKickbackTaxRecords(year)
    ])
      .then(data =>
        setResult({
          loading: false,
          data: data
            .flat()
            .map(formatRow(assets))
            .sort((a, b) => b.created - a.created),
          error: null
        })
      )
      .catch(e => setResult({ loading: false, data: null, error: e }));
  }, [skip, accountId, year, assets]);

  return result;
};
